import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Grid } from '@mui/material';
import { Button, Dialog } from '@totalsoft/rocket-ui';
import { useTranslation } from 'react-i18next';
import { useCrossCheckUtils } from '../utils';
import { emptyString } from 'constants/defaultValues';
import {
  ComparisonContainer,
  DirectSettlementAvatar,
  ComparisonCard,
  RcaComparisonDetail,
  RcaComparisonTitle,
  CrossCheckTitle,
  CrossCheckTitleContainer
} from '../FlowCheckStyles';
import { DetatchedAccordion } from 'components/common/SurfaceStyles';

const RcaCrossCheckComparatorModal = ({ onCloseModal, existingQuotations, newQuotations }) => {
  const { t } = useTranslation();
  const { getRcaCrossCheckComparison } = useCrossCheckUtils();

  const comparisons = useMemo(() => {
    const periods = Array.from(new Set([...existingQuotations, ...newQuotations].map(x => x.period)));
    const result = [];
    for (const period of periods) {
      const existingQuotationsForPeriod = existingQuotations.filter(x => x.period === period);
      const newQuotationsForPeriod = newQuotations.filter(x => x.period === period);
      const insurers = [...existingQuotationsForPeriod, ...newQuotationsForPeriod].map(x => x.insurerName).sort();
      const insurersSet = Array.from(new Set(insurers));

      result.push({
        period,
        data: insurersSet.map(insurer => {
          const existingQuotation = existingQuotationsForPeriod.find(x => x.insurerName === insurer);
          const newQuotation = newQuotationsForPeriod.find(x => x.insurerName === insurer);
          return [
            getRcaCrossCheckComparison(existingQuotation, newQuotation, 'error'),
            getRcaCrossCheckComparison(newQuotation, existingQuotation, 'success')
          ];
        })
      });
    }
    return result;
  }, [existingQuotations, newQuotations, getRcaCrossCheckComparison]);

  return (
    <Dialog
      id='rca-cross-check-dialog'
      open
      fullWidth
      maxWidth={'md'}
      onClose={onCloseModal}
      closeButtonProps={{ sx: { display: 'none' } }}
      sx={{ '& .MuiDialogTitle-root': { p: 0 } }}
      title={
        <CrossCheckTitleContainer>
          <CrossCheckTitle>{t('Settings.InsuranceIntegrationChecks.RcaCheck.QuotationsThen')}</CrossCheckTitle>
          <CrossCheckTitle>{t('Settings.InsuranceIntegrationChecks.RcaCheck.QuotationsNow')}</CrossCheckTitle>
        </CrossCheckTitleContainer>
      }
      content={comparisons.map((period, idxComparison) => (
        <DetatchedAccordion
          key={idxComparison}
          defaultExpanded={true}
          sx={{ my: 2 }}
          title={
            <RcaComparisonTitle>
              {t('Settings.InsuranceIntegrationChecks.RcaCheck.OffersAndPeriod', {
                months: period.period
              })}
            </RcaComparisonTitle>
          }
          content={
            <Grid item container xs={12}>
              {period.data.map((comparison, idxPeriod) => (
                <Grid item container key={`${idxComparison}_${idxPeriod}`}>
                  {comparison.map(({ color, data }, idxQuotation) => (
                    <ComparisonContainer
                      item
                      container
                      xs={6}
                      key={`${idxComparison}_${idxPeriod}_${idxQuotation}`}
                      existing={idxQuotation === 0}
                    >
                      <ComparisonCard item container xs={12} alignItems='center'>
                        <Grid item xs={12} sm={8} md={4}>
                          <strong>{data.insurerName.value}:</strong>
                        </Grid>
                        <Grid item xs={12} md={4} p={0.5}>
                          <RcaComparisonDetail
                            item
                            container
                            alignItems={'center'}
                            justifyContent={'center'}
                            changed={data.insuranceAmountWithoutDD.changed}
                            changedColor={color}
                          >
                            <span>
                              {data.insuranceAmountWithoutDD.value
                                ? `${t('DECIMAL2', {
                                    number: data.insuranceAmountWithoutDD.value || emptyString
                                  })} ${data.currencyCode.value}`
                                : 'N/A'}
                            </span>
                          </RcaComparisonDetail>
                        </Grid>
                        <Grid item xs={12} md={4} p={0.5}>
                          <RcaComparisonDetail
                            item
                            container
                            alignItems={'center'}
                            justifyContent={'center'}
                            wrap='nowrap'
                            changed={data.insuranceAmountWithDD.changed}
                            changedColor={color}
                          >
                            <DirectSettlementAvatar>{t('Offer.Edit.Comparator.DirectSettlementCaption')}</DirectSettlementAvatar>
                            <span>
                              {data.insuranceAmountWithDD.value
                                ? `${t('DECIMAL2', {
                                    number: data.insuranceAmountWithDD.value || emptyString
                                  })} ${data.currencyCode.value}`
                                : 'N/A'}
                            </span>
                          </RcaComparisonDetail>
                        </Grid>
                      </ComparisonCard>
                    </ComparisonContainer>
                  ))}
                </Grid>
              ))}
            </Grid>
          }
        />
      ))}
      actions={
        <Box mr={1} mb={1}>
          <Button color={'primary'} onClick={onCloseModal}>
            {t('General.Close')}
          </Button>
        </Box>
      }
    />
  );
};

RcaCrossCheckComparatorModal.propTypes = {
  onCloseModal: PropTypes.func.isRequired,
  existingQuotations: PropTypes.array,
  newQuotations: PropTypes.array
};

export default RcaCrossCheckComparatorModal;
